<script setup>
import { useAdminStore } from "@/stores";
import useVuelidate from "@vuelidate/core";
import {
  email,
  helpers,
  minLength,
  required,
  sameAs,
} from "@vuelidate/validators";
import { computed, onMounted, ref, watch } from "vue";

import roles from "@/config/lists/roles";
import userResponsibilities from "@/config/lists/userResponsibilities";

const adminStore = useAdminStore();

const form = ref({});

const loading = ref(false);

const passwordRef = computed(() => form.value.password);

const passwordFilled = computed(() => {
  return form.value.password && form.value.password.length > 0;
});

const conditionalMinLength = helpers.withMessage(
  "Password must be at least 8 characters",
  (value) => {
    if (!value || value.length === 0) return true;
    return value.length >= 8;
  }
);

const confirmPasswordRequiredIfPassword = helpers.withMessage(
  "Confirm password is required",
  (value) => {
    if (!passwordFilled.value) return true;
    return value != null && value.length > 0;
  }
);

const confirmPasswordMatches = helpers.withMessage(
  "Passwords do not match",
  (value) => {
    if (!passwordFilled.value) return true;
    return value === form.value.password;
  }
);

const showPass = ref(false);

const showConfPass = ref(false);

async function handleSubmit() {
  const isValid = await v$.value.$validate();
  if (!isValid) {
    return;
  }
  loading.value = true;
  await adminStore.editUser(form.value);
  loading.value = false;
}

function removeImage() {
  form.value.imageLink = null;
}

const rules = {
  email: {
    required: helpers.withMessage("Email is required", required),
    email: helpers.withMessage("Invalid email format", email),
  },
  password: {
    minLength: conditionalMinLength,
  },
  confirmPassword: {
    required: confirmPasswordRequiredIfPassword,
    sameAsPassword: confirmPasswordMatches,
  },
};

const v$ = useVuelidate(rules, form);

const getErrorMessages = (field) =>
  computed(() =>
    v$.value[field]?.$error
      ? v$.value[field].$errors.map((e) => e.$message)
      : []
  );

onMounted(async () => {
  const id = window.location.pathname.split("/").filter(Boolean).pop();
  //const user = await adminStore.getUserData({ id: id });
  await adminStore.getUserData({ id: id });
  //form.value = { ...adminStore.user };
  form.value.password = null;
  form.value.confirmPassword = null;
});

watch(
  () => adminStore.user,
  (newUser, oldUser) => {
    console.log("User changed:", newUser);

    // Example: sync to form
    if (newUser) {
      form.value = {
        ...newUser,
        password: null,
        confirmPassword: null,
      };
    }
  },
  {
    immediate: true,
    deep: true,
  }
);
</script>

<template>
  <div class="d-flex flex-column pa-5 pa-sm-10">
    <h1 class="mb-10 page-title">User Settings</h1>
    <div class="d-flex form-wrapper">
      <v-form class="d-flex flex-column gap-10 w-100">
        <v-file-input
          v-if="
            form.imageLink == null ||
            (form.imageLink != null && form.imageLink.trim() == '')
          "
          v-model="form.image"
          accept="image/*"
          label="Upload Photo"
          variant="outlined"
          prepend-icon=""
          prepend-inner-icon="mdi-camera"
          hide-details="auto"
        />

        <div
          v-if="form.imageLink != null && form.imageLink.trim() != ''"
          class="image-preview d-flex flex-column gap-10 align-center justify-space-between"
        >
          <img :src="form.imageLink" />
          <v-btn
            size="small"
            color="error"
            variant="outlined"
            @click="removeImage()"
          >
            Remove Image
          </v-btn>
        </div>
        <v-text-field
          id="id"
          v-model="form.name"
          name="name"
          label="Name"
          :error-messages="getErrorMessages('name').value"
          hide-details="auto"
          @blur="v$.name.$touch()"
        />
        <v-text-field
          id="id"
          v-model="form.email"
          name="email"
          type="email"
          variant="outlined"
          :error-messages="getErrorMessages('email').value"
          hide-details="auto"
          @blur="v$.email.$touch()"
        >
          <template #label> Email </template>
        </v-text-field>
        <v-text-field
          id="id"
          v-model="form.password"
          name="password"
          :type="showPass ? 'text' : 'password'"
          label="Password"
          variant="outlined"
          :append-inner-icon="showPass ? 'mdi-eye' : 'mdi-eye-off'"
          :error-messages="getErrorMessages('password').value"
          hide-details="auto"
          @click:append-inner="showPass = !showPass"
          @blur="v$.password.$touch()"
        />

        <v-text-field
          id="id"
          v-model="form.confirmPassword"
          name="password"
          :type="showConfPass ? 'text' : 'password'"
          label="Confirm Password"
          variant="outlined"
          :append-inner-icon="showConfPass ? 'mdi-eye' : 'mdi-eye-off'"
          :error-messages="getErrorMessages('confirmPassword').value"
          hide-details="auto"
          @click:append-inner="showConfPass = !showConfPass"
          @blur="v$.confirmPassword.$touch()"
        />

        <Autocomplete
          v-model="form.roles"
          label="User roles"
          :items="roles"
          :multiple="true"
          :chips="true"
        />

        <Autocomplete
          v-model="form.userResponsibilities"
          label="User responsibilities"
          :items="userResponsibilities"
          :multiple="true"
          :chips="true"
        />

        <div class="mb-5 d-flex justify-space-between align-center">
          <v-btn
            color="primary"
            :loading="loading"
            :disabled="loading"
            @click="handleSubmit()"
          >
            Save changes
          </v-btn>
        </div>
      </v-form>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.image-preview {
  img {
    max-width: 150px;
    border-radius: 8px;
  }
}

.form-wrapper {
  width: 100%;
}

@media (min-width: 600px) {
  .form-wrapper {
    width: 50%;
  }
}
</style>
